import React, { useEffect, useState } from "react";
import { Skeleton, Grid, Stack, Typography, Button } from "@mui/material";
import { ForecastReviewGrid } from "../../Components/ForecastReviewGrid";
import ProductMultiSelection from "../../Components/ProductMultiSelection";
import { useForecastReviewContext } from "../../Contexts/forecastReviewContext";
import { useDistributorsContext } from "../../Contexts/distributorsContext";
import { CustomerType } from "../../Enums/CustomerType";
import { useProductsContext } from "../../Contexts/productsContext";
import DistributorMultiselection from "../../Components/DistributorMultiSelection";
import PropTypes from 'prop-types';
import { DISTRIBUTOR_LOCAL_STORAGE_KEY, SKU_LOCAL_STORAGE_KEY } from "../../Utilities/constants.ts";

export const ForecastReport = ({
  editableWeeksStartIndex,
  editableWeeksEndIndex,
  startWeek,
  endWeek
}) => {
  const { selectedDistributors, setSelectedDistributors } = useDistributorsContext();
  const { selectedProducts, setSelectedProducts } = useProductsContext();
  const {
    isReady: areForecastReviewItemsReady,
    forecastReviewItems,
    getFormattedOriginalForecastReviewItems,
    setForecastReviewItems,
    getForecastReport
  } = useForecastReviewContext();
  const [exporting, setExporting] = useState(false);
  const [isDistributorDropdownOpen, setIsDistributorDropdownOpen] = useState(false);

  useEffect(() => {
    const storedSku = localStorage.getItem(SKU_LOCAL_STORAGE_KEY)
    if (storedSku) {
      setSelectedProducts(JSON.parse(storedSku));
    }

    const storedDistributor = localStorage.getItem(DISTRIBUTOR_LOCAL_STORAGE_KEY)
    if (storedDistributor && storedDistributor !== "undefined") {
      setSelectedDistributors(JSON.parse(storedDistributor));
      getForecastReport();
    }
  }, []);

  useEffect(() => {
    if (selectedDistributors && !isDistributorDropdownOpen) {
      getForecastReport();
    }
    if (selectedDistributors) {
      localStorage.setItem(DISTRIBUTOR_LOCAL_STORAGE_KEY, JSON.stringify(selectedDistributors));
    }
  }, [selectedDistributors]);

  useEffect(() => {
    if (forecastReviewItems?.length === 0) {
      const selectedSKUs = selectedProducts?.map(selectedProduct => selectedProduct.sku);
      setForecastReviewItems(getFormattedOriginalForecastReviewItems().filter(forecastReviewItem => selectedSKUs.length > 0 ? selectedSKUs.includes(forecastReviewItem.product.sku) : true));
    } else {
      localStorage.setItem(SKU_LOCAL_STORAGE_KEY, JSON.stringify(selectedProducts));
    }
  }, [selectedProducts, areForecastReviewItemsReady, setSelectedProducts]);

  const exportForecast = () => {
    setExporting(true);

    let distributors = "";
    selectedDistributors.forEach(distributor => distributors += `distributorIds=${distributor.distributorId}&`);

    let products = "";
    selectedProducts.forEach(product => products += `productSKUs=${product.sku}&`);

    window.open(`api/Distributor/Forecasts/reportCsvFile?${distributors}${products}`);

    setExporting(false);
  };

  return (
    <React.Fragment>
      <Grid container columnSpacing={0} padding={0} justifyContent="center" alignItems="center">
        <Typography variant="h5">Forecast Report</Typography>
      </Grid>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center" sx={{ pl: 2, pr: 2 }}>
        <DistributorMultiselection
          customerType={CustomerType.VMI}
          filterByCanOrderBeer={true}
          onOpen={() => setIsDistributorDropdownOpen(true)}
          onClose={() => { setIsDistributorDropdownOpen(false); getForecastReport(); }}
        />
        <ProductMultiSelection />
        <Stack direction="row" spacing={2} alignItems="center" sx={{ ml: 'auto' }}>
          <Button
            variant="outlined"
            disabled={forecastReviewItems.length === 0}
            onClick={() => {
              setForecastReviewItems([]);
              getForecastReport();
            }}
          >
            Reset
          </Button>
          <Button
            variant="outlined"
            disabled={forecastReviewItems.length === 0 && !exporting}
            onClick={exportForecast}
          >
            Export
          </Button>
        </Stack>
      </Stack>

      {!selectedDistributors || selectedDistributors.length === 0 || !areForecastReviewItemsReady ? (
        selectedDistributors && selectedDistributors.length > 0 && !areForecastReviewItemsReady ? (
          <Skeleton variant="rectangular" height={400} />
        ) : (
          <center style={{ padding: 25 }}>
            <h4>Please select a distributor</h4>
          </center>
        )
      ) : forecastReviewItems.length === 0 ? (
        <center style={{ padding: 25 }}>
          <h4>There is no historical data for the selected filter</h4>
          <h5>
            If you believe this is an error, please contact your business
            partner or the helpdesk at
            <a href="mailto: customerservice@bostonbeer.com"> customerservice@bostonbeer.com</a>
          </h5>
        </center>
      ) : (
        <ForecastReviewGrid editableWeeksStartIndex={editableWeeksStartIndex} editableWeeksEndIndex={editableWeeksEndIndex} startWeek={startWeek} endWeek={endWeek} isHistorical={true} />
      )}
    </React.Fragment >
  );
};

ForecastReport.propTypes = {
  editableWeeksStartIndex: PropTypes.number,
  editableWeeksEndIndex: PropTypes.number,
  startWeek: PropTypes.number,
  endWeek: PropTypes.number
}
